import React, { useState } from "react";
import Link from "next/link";
import { StepName, StepProps } from "../../types";
import { useWebTranslation } from "translations";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import Colors from "ui-kit/colors";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import { StackedTextInput } from "ui-kit/TextInput/TextInput";
import {
  formatErrorMessage,
  ssnFormatter,
  validateEmail,
  validatePhone,
  validateSsn,
} from "utils/validation";
import { getSiteUrl } from "utils/site";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import {
  useSignupDispatch,
  useSignupStore,
} from "components/Signup/store/hooks";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { useCustomer } from "components/Signup/store/selectors/customer";
import { upsertDraft } from "components/Signup/store/slices/animals";
import {
  useError,
  useIsLoading,
} from "components/Signup/store/selectors/metadata";
import AcceptBox from "components/Signup/components/AcceptBoxSwe/AcceptBox";
import { trackSignupEvent } from "components/Signup/analytics";

const UserInfo = ({ next, previous, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();
  const [ssnValid, setSsnValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValid, setPhoneValid] = useState(true);

  const { dispatch, getState } = useSignupStore();
  const { email, phone, ssn, marketingPermissionAccepted } = useCustomer();
  const error = useError();
  const loading = useIsLoading();

  return (
    <ChatContainer leaving={transitioning}>
      <style jsx>{`
        /* Not very pretty but just testing the concept */
        input:focus:invalid {
          border: 1px solid ${Colors.orange300};
          border-radius: 8px;
        }
      `}</style>
      <ChatMessage>
        <Typography>{t("signup.user_info.text_1")}</Typography>
      </ChatMessage>
      <ChatMessage>
        <Typography>{t("signup.user_info.text_2")}</Typography>
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage fromMe border={false}>
        <StackedTextInput
          first
          placeHolder="Personnummer (ååmmdd-xxxx)"
          value={ssn ?? ""}
          onChangeText={(ssn) => {
            const formattedSsn = ssnFormatter(ssn);

            if (!ssnValid) {
              setSsnValid(validateSsn(formattedSsn));
            }

            dispatch(updateCustomer({ ssn: formattedSsn }));
          }}
          onBlur={(e) => {
            setSsnValid(validateSsn(e.target.value));
          }}
          maxLength={13}
        />
        <StackedTextInput
          type="email"
          placeHolder={t("global.email")}
          value={email ?? ""}
          onChangeText={(email) => {
            if (!emailValid) {
              setEmailValid(validateEmail(email));
            }

            dispatch(updateCustomer({ email }));
          }}
          onBlur={(e) => {
            setEmailValid(validateEmail(e.target.value));
          }}
          autoComplete="email"
        />
        <StackedTextInput
          type="tel"
          autoComplete="phone"
          last
          placeHolder={t("global.phone")}
          value={phone ?? ""}
          onChangeText={(userPhone) => {
            const formattedPhone = userPhone.replace(/[^\d+-\s]+/g, "");

            if (!phoneValid) {
              setPhoneValid(validatePhone(formattedPhone));
            }

            dispatch(updateCustomer({ phone: formattedPhone }));
          }}
          onBlur={(e) => {
            setPhoneValid(validatePhone(e.target.value));
          }}
        />
        <div style={{ marginLeft: 8, marginTop: 8 }}>
          {!ssnValid && (
            <>
              <Gutter size={1} />
              <Typography color={"error"} testId="date-error">
                {t("validation.ssn_invalid_format")}
              </Typography>
              <Gutter size={1} />
            </>
          )}
          {!emailValid && (
            <>
              <Gutter size={1} />
              <Typography color={"error"} testId="date-error">
                {t("validation.email")}
              </Typography>
              <Gutter size={1} />
            </>
          )}
          {phone && !phoneValid && (
            <>
              <Gutter size={1} />
              <Typography color={"error"} testId="date-error">
                {t("validation.phone")}
              </Typography>
              <Gutter size={1} />
            </>
          )}
          <AcceptBox
            textKey="signup.user_info.marketing_permission.description"
            selected={marketingPermissionAccepted}
            links={[]}
            onSelect={() => {
              if (!marketingPermissionAccepted) {
                trackSignupEvent(
                  "INSURANCE_SIGNUP_CRM_OPTIN" as StepName,
                  0,
                  getState()
                );
              }

              dispatch(
                updateCustomer({
                  marketingPermissionAccepted: !marketingPermissionAccepted,
                })
              );
            }}
          />
          <Typography variant="body3" color="grey400">
            <span>{t("signup.user_info.email.text")}&nbsp;</span>
            <Link href={getSiteUrl("se/integritet")} legacyBehavior>
              <a target="_blank">
                <Typography
                  variant="body3"
                  color="black"
                  style={{ textDecoration: "underline" }}
                >
                  {t("signup.user_info.email.note")}
                </Typography>
              </a>
            </Link>
            .
          </Typography>
        </div>
      </ChatMessage>
      {!!error && (
        <ErrorMessage>
          <Typography color="error">{formatErrorMessage(error)}</Typography>
        </ErrorMessage>
      )}

      <FlowButtons
        inProgress={transitioning || loading}
        next={() => {
          dispatch(upsertDraft(undefined, { navigateToNextOnSuccess: true }));
        }}
        disabled={!isValid}
        previous={previous}
        backButton={false}
      />
    </ChatContainer>
  );
};

export default UserInfo;
